import {useMeStore} from '@progyny/store/meStore'
import {
  FERTILITY,
  MENOPAUSE,
  PREGNANCY_AND_POSTPARTUM,
} from '@progyny/utils/constants'

export interface UseProductLineReturn {
  /** Current product line string (FERTILITY | MENOPAUSE | PREGNANCY_AND_POSTPARTUM) */
  productLine: string
  isFertility: boolean
  isMenopause: boolean
  isP3: boolean
}

/**
 * Hook to get the current product line and utility booleans.
 *
 * The value is derived directly from the user store's `user`/`isLoggedIn` on every
 * render, with no cross-render caching (e.g. no `window` global, no local
 * state synced via an effect). `me` (Apollo/user store) is the single source
 * of truth, so there is no window in which this hook can report a stale
 * product line relative to it - whether that's a hard refresh, a same-tab
 * logout followed immediately by logging back in (both SPA navigations, no
 * reload), or switching journeys. All of those previously relied on timing
 * between this hook's effects and a `window.productLineState` cache, which
 * could race with `Routes` (src/routes/index.tsx) reading `productLine`
 * mid-transition and force-redirecting into onboarding with the wrong
 * journey (e.g. a pregnancy_and_postpartum member briefly read as
 * fertility, incorrectly redirecting to set-up-profile/birth-assigned-sex).
 *
 * Returns FERTILITY (default) when the member is not logged in or
 * `activeMemberJourney` is unset.
 * Use: const { productLine } = useProductLine() or const { productLine, isFertility } = useProductLine()
 */
export const useProductLine = (): UseProductLineReturn => {
  // Get activeMemberJourney and login status from me context
  const user = useMeStore((s) => s.user)
  const isLoggedIn = useMeStore((s) => s.isLoggedIn)
  const meProductLine = user?.member?.activeMemberJourney || null

  const current = isLoggedIn && meProductLine ? meProductLine : FERTILITY

  return {
    productLine: current,
    isFertility: current === FERTILITY,
    isMenopause: current === MENOPAUSE,
    isP3: current === PREGNANCY_AND_POSTPARTUM,
  }
}
